import type { PlatformControl } from '@/lib/platform/control';

export const CONTROL_SECTIONS = [
  { id: 'overview', label: 'Overview', group: 'Operate', blurb: 'What was measured on the last check. Unknown means this process cannot see it.' },
  { id: 'databases', label: 'Databases', group: 'Operate', blurb: 'Recheck opens that agency database again. Schema repair stays on the agency page.' },
  { id: 'jobs', label: 'Jobs', group: 'Operate', blurb: 'Retry queues one failed job again. The payload stays on the server.' },
  { id: 'backups', label: 'Backups', group: 'Operate', blurb: 'Archives already written. Start a new one from the agency page. A checksum is not a restore drill.' },
  { id: 'configuration', label: 'Configuration', group: 'Configure', blurb: 'Support details, the desk timezone, and the maintenance message. Domains stay in the server environment.' },
  { id: 'integrations', label: 'Integrations', group: 'Configure', blurb: 'Assistant, mail, and SMS credentials. A saved key is not connected until a test is recorded.' },
  { id: 'security', label: 'Security', group: 'Configure', blurb: 'Authenticator sign-in stays required. The name applies to the next enrollment.' },
  { id: 'audit', label: 'Audit', group: 'Records', blurb: 'Append-only platform trail. Passwords, tokens, and authenticator secrets are never stored.' },
] as const;

export const CONTROL_GROUPS = ['Operate', 'Configure', 'Records'] as const;

export type ControlSectionId = (typeof CONTROL_SECTIONS)[number]['id'];

const SIGNAL_DESTINATIONS: Record<string, ControlSectionId> = {
  central_database: 'databases',
  tenants: 'databases',
  backups: 'backups',
  scheduler: 'jobs',
  queue: 'jobs',
  maintenance: 'configuration',
  mail: 'integrations',
  sms: 'integrations',
  ai: 'integrations',
  storage: 'integrations',
};

export function isControlSection(value: string): value is ControlSectionId {
  return CONTROL_SECTIONS.some((item) => item.id === value);
}

export function controlSectionPath(id: ControlSectionId): string {
  return id === 'overview' ? '/super-admin/settings' : `/super-admin/settings/${id}`;
}

export function controlSectionFromPath(pathname: string): ControlSectionId | null {
  if (pathname === '/super-admin/settings') return 'overview';
  if (!pathname.startsWith('/super-admin/settings/')) return null;
  const id = pathname.slice('/super-admin/settings/'.length).split('/')[0] ?? '';
  return isControlSection(id) ? id : null;
}

export function signalDestination(key: string): string | null {
  const section = SIGNAL_DESTINATIONS[key];
  return section === undefined ? null : controlSectionPath(section);
}

export function controlSectionAlert(id: ControlSectionId, control: PlatformControl | null): number {
  if (!control) return 0;
  const needsAttention = (state: string) => state === 'degraded' || state === 'unavailable' || state === 'stale';
  if (id === 'overview') return control.signals.filter((signal) => needsAttention(signal.state)).length;
  if (id === 'databases') return control.tenants.filter((tenant) => tenant.database === 'unavailable').length;
  if (id === 'jobs') return control.failedJobs.count;
  if (id === 'backups') return control.signals.some((signal) => signal.key === 'backups' && needsAttention(signal.state)) ? 1 : 0;
  if (id === 'integrations') return control.integrations.filter((item) => item.testable && needsAttention(item.state)).length;
  return 0;
}
